import { useEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { ROUTES } from '../routes'
import { COOKIE_CHOICE_EVENT, deleteGaCookies, readCookieChoice, type CookieChoice } from '../cookieConsent'

/**
 * The community's own Google Analytics (GA4), when it set a measurement ID.
 *
 * Nothing is sent without one. Events carry no personal data: no e-mail, no
 * name, no document, and no `user_id`. Ids in events are the platform's own
 * opaque ids (product, purchase).
 *
 * F-201: nothing of Google loads before the visitor accepts the cookie notice
 * (`cookieConsent.ts`). The cookie stays on the community's host
 * (`cookie_domain: 'none'`). Refusing drops the queued events, turns GA off on
 * this page if it had loaded, and removes `_ga` from the host and parent domain.
 */

let isInitialized = false
let measurementId: string | null = null
let communityCurrency: string | null = null
/* null until the visitor decides; while denied nothing is queued or sent. */
let consent: CookieChoice | null = null

/* SPA page views: one per route, sent once the title has settled (a space's
   title arrives after its data). The path that was sent last is never sent
   again until the route changes, so a re-render cannot duplicate it. */
const PAGE_VIEW_SETTLE_MS = 700
let lastSentPage: string | null = null
let pendingPage: { path: string; title?: string } | null = null
let pageTimer: number | null = null

/* Events raised before the community (and its GA id) loaded, e.g. a purchase
   confirmed on a payment page opened cold. Sent on init; capped, since a
   community without GA never drains it. */
const MAX_QUEUED_EVENTS = 50
const queuedEvents: Array<[string, Record<string, unknown>]> = []

export function useGoogleAnalytics(googleAnalyticsId: string | null | undefined, currency?: string | null, communityId?: string | null) {
  const [choice, setChoice] = useState<CookieChoice | null>(() => readCookieChoice(communityId))

  useEffect(() => {
    if (currency) communityCurrency = currency
  }, [currency])

  useEffect(() => {
    setChoice(readCookieChoice(communityId))
    const onChoice = (event: Event) => {
      const detail = (event as CustomEvent<{ communityId: string; choice: CookieChoice }>).detail
      if (detail?.communityId === communityId) setChoice(detail.choice)
    }
    window.addEventListener(COOKIE_CHOICE_EVENT, onChoice)
    return () => window.removeEventListener(COOKIE_CHOICE_EVENT, onChoice)
  }, [communityId])

  useEffect(() => {
    const id = googleAnalyticsId?.trim()
    consent = choice
    if (choice === 'denied') {
      queuedEvents.length = 0
      pendingPage = null
      if (pageTimer) {
        window.clearTimeout(pageTimer)
        pageTimer = null
      }
      if (measurementId) disableGa(measurementId, true)
      deleteGaCookies()
      return
    }
    if (!id || choice !== 'granted') return
    if (isInitialized) {
      // Accepted again after refusing on this page: GA is already loaded, only switched off.
      disableGa(id, false)
      return
    }

    ReactGA.initialize(id, { gtagOptions: { send_page_view: false, cookie_domain: 'none' } })
    measurementId = id
    isInitialized = true
    // The route that loaded before the community did is sent now, then what happened on it.
    flushPageView()
    while (queuedEvents.length) {
      const [name, params] = queuedEvents.shift()!
      ReactGA.event(name, params)
    }
  }, [googleAnalyticsId, choice])
}

/** GA's own opt-out flag (`window['ga-disable-<id>']`): gtag stops sending while it is true. */
function disableGa(id: string, disabled: boolean) {
  ;(window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = disabled
}

const isSending = () => isInitialized && consent === 'granted'

/**
 * The path as GA sees it: no query or hash (they can carry tokens, e.g. the
 * set-password link), a member's own pages collapsed, and the token of an
 * offer or invite link replaced.
 */
export function sanitizePath(path: string): string {
  let clean = path.split(/[?#]/)[0] || '/'

  for (const prefix of [ROUTES.downsellOffer, ROUTES.inviteDecline, ROUTES.emailUnsubscribe]) {
    if (clean.startsWith(prefix) && clean.length > prefix.length) clean = `${prefix}:token`
  }

  if (clean.includes('/user/')) {
    const segments = clean.split('/')
    const userIndex = segments.indexOf('user')
    if (userIndex !== -1 && segments[userIndex + 2]) {
      clean = `/${segments[userIndex]}/${segments[userIndex + 2]}`
    }
  }
  return clean
}

function flushPageView() {
  if (pageTimer) {
    window.clearTimeout(pageTimer)
    pageTimer = null
  }
  if (!isSending() || !pendingPage) return
  const { path, title } = pendingPage
  pendingPage = null
  if (path === lastSentPage) return
  lastSentPage = path

  /* Every later hit on this route reports the clean URL too, not the
     address bar (whose query can carry a token). */
  ReactGA.gtag('set', { page_location: `${window.location.origin}${path}` })
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: title || document.title,
    location: `${window.location.origin}${path}`
  })
}

/**
 * A route change. Called on every render of the route; only a new path is
 * sent, after it has been stable for a moment, with the title it settled on.
 */
export function trackPageView(path: string, title?: string) {
  if (consent === 'denied') return
  const page = sanitizePath(path)
  if (page === lastSentPage && !pendingPage) return
  pendingPage = { path: page, title }
  if (pageTimer) window.clearTimeout(pageTimer)
  pageTimer = window.setTimeout(flushPageView, PAGE_VIEW_SETTLE_MS)
}

type EventParams = Record<string, unknown>

export function trackEvent(eventName: string, eventParams?: EventParams, options?: { transport?: 'beacon' }) {
  const params = {
    ...eventParams,
    ...(options?.transport && { transport_type: options.transport })
  }
  if (consent === 'denied') return
  if (!isSending()) {
    if (queuedEvents.length < MAX_QUEUED_EVENTS) queuedEvents.push([eventName, params])
    return
  }
  // A pending page view goes first, so events land on the page they happened on.
  if (pendingPage) flushPageView()
  ReactGA.event(eventName, params)
}

export function isGAInitialized() {
  return isSending()
}

/** The community's currency, for events whose source does not carry one. */
export function gaCurrency(fallback = 'BRL') {
  return communityCurrency || fallback
}

/**
 * GA's own ids for this browser (`client_id` from the `_ga` cookie, and the
 * session), sent with a checkout so the server-side `purchase` (Measurement
 * Protocol) lands on the same user and session as the browser's. Empty when
 * GA is not on, or does not answer in time.
 */
export function getGaIds(timeoutMs = 400): Promise<{ gaClientId?: string; gaSessionId?: string }> {
  if (!isSending() || !measurementId) return Promise.resolve({})
  const id = measurementId
  const read = (field: 'client_id' | 'session_id') =>
    new Promise<string | undefined>((resolve) => {
      const timer = window.setTimeout(() => resolve(undefined), timeoutMs)
      try {
        ReactGA.gtag('get', id, field, (value: unknown) => {
          window.clearTimeout(timer)
          resolve(value === undefined || value === null || value === '' ? undefined : String(value))
        })
      } catch {
        window.clearTimeout(timer)
        resolve(undefined)
      }
    })
  return Promise.all([read('client_id'), read('session_id')]).then(([gaClientId, gaSessionId]) => ({
    ...(gaClientId ? { gaClientId } : {}),
    ...(gaSessionId ? { gaSessionId } : {})
  }))
}
